/**
 * CompletedCard — completed session row in TestsHub.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/tests.jsx CompletedCard
 */

import * as React from 'react'
import { clsx } from 'clsx'
import { fmtDate, fmtTime } from '@arianna/reader-core'
import { ScoreBar } from './primitives.js'

export interface CompletedCardSession {
  id: string
  completed_at: number | null
  score: { correct: number; wrong: number; skipped: number; total: number } | null
}

export interface CompletedCardProps {
  session: CompletedCardSession
  /** True for the most-recent completed session (shows "Ultima" badge) */
  isFirst: boolean
  onView: () => void
}

export function CompletedCard({ session, isFirst, onView }: CompletedCardProps) {
  const score = session.score
  if (score == null || session.completed_at == null) return null

  const pct = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0
  // Score color: green ≥75%, gold ≥50%, red <50%
  const scoreColorClass = pct >= 75 ? 'text-green' : pct >= 50 ? 'text-gold' : 'text-red'

  return (
    <div className="relative bg-surface border border-line rounded-[12px] px-5 py-4 flex items-center gap-5 font-sans transition-colors duration-150">
      {/* "Ultima" badge */}
      {isFirst && (
        <div className="absolute top-[-1px] right-[14px] text-[9px] tracking-[0.1em] uppercase font-bold text-bg bg-ink-3 px-2 py-[3px] rounded-b-[6px]">
          Ultima
        </div>
      )}

      {/* Score badge */}
      {/* design handoff: width 64px */}
      <div className="shrink-0 w-[64px] text-center">
        <div
          className={clsx(
            /* design handoff: fontSize 32px */
            'font-mono text-[32px] font-semibold leading-none tracking-[-0.03em]',
            scoreColorClass,
          )}
        >
          {score.correct}
          {/* design handoff: fontSize 16px */}
          <span className="text-[16px] text-ink-4 font-normal">/{score.total}</span>
        </div>
        <div className="text-[10px] text-ink-4 mt-1">{pct}%</div>
      </div>

      {/* Score bar + stats */}
      <div className="flex-1 min-w-0">
        <ScoreBar
          correct={score.correct}
          wrong={score.wrong}
          skipped={score.skipped}
          total={score.total}
        />
        {/* design handoff: gap 14px, fontSize 11.5px */}
        <div className="flex gap-[14px] mt-[9px] text-[11.5px] text-ink-3 flex-wrap">
          <span className="flex items-center gap-[5px]">
            <span className="w-[7px] h-[7px] rounded-full bg-green shrink-0" />
            {score.correct} corrette
          </span>
          <span className="flex items-center gap-[5px]">
            <span className="w-[7px] h-[7px] rounded-full bg-red shrink-0" />
            {score.wrong} errate
          </span>
          {score.skipped > 0 && (
            <span className="flex items-center gap-[5px]">
              <span className="w-[7px] h-[7px] rounded-full bg-ink-4 shrink-0" />
              {score.skipped} saltate
            </span>
          )}
        </div>
      </div>

      {/* Date + CTA */}
      <div className="shrink-0 flex flex-col items-end gap-[9px]">
        <div className="text-right">
          <div className="font-mono text-xs text-ink-3 font-medium">
            {fmtDate(session.completed_at)}
          </div>
          <div className="font-mono text-[10px] text-ink-4">{fmtTime(session.completed_at)}</div>
        </div>
        <button
          onClick={onView}
          /* design handoff: borderRadius 7px */
          className="px-[14px] py-[7px] text-xs font-medium bg-transparent text-ink-2 border border-line rounded-[7px] cursor-pointer font-sans whitespace-nowrap transition-colors duration-150"
        >
          Rivedi →
        </button>
      </div>
    </div>
  )
}
